import { useState } from 'react'
import { Button, Field } from '../ui'
import { KiezMark } from '../components/KiezMark'
import { useAuth } from '../lib/auth'
import { authLinkFromHash } from '../lib/authLink'
import { supabase } from '../lib/supabase'
import { PRIVACY_NOTE } from '../content/privacy'
import { CANCELLED } from '../lib/passkeyError'
import { currentRegion, useRegionsNow } from '../lib/regions'

/**
 * Passkey first, password as the way in.
 *
 * Passkeys bind to the Relying Party ID, which is the bare domain, so they work
 * on the deployed app and not on localhost. Password sign-in is therefore not a
 * legacy fallback — it is how development works, and how the first passkey gets
 * registered in the first place.
 */
// U5.1: which kind of email link this visit came by, for the heading. `auth.tsx`
// reads the same hash for its own flow and keeps the result private; the hash
// is gone once supabase-js has read it, so this reads it too, at load, in the
// same task (App.tsx imports this module statically). Null on a plain visit.
const LINK_KIND = typeof location === 'undefined' ? null : authLinkFromHash(location.hash).kind

/**
 * Where a password-reset mail sends the person back to: the origin they are on
 * when it is one of ours, else the canonical kiez.page. `redirectTo` has to be
 * on Supabase's allowlist; an arbitrary origin (a preview, a typo'd host)
 * would be refused or, worse, honoured.
 */
const OWN_ORIGINS = ['https://kiez.page', 'https://app.schnauzelist.com']
function resetRedirect(): string {
  if (typeof location === 'undefined') return OWN_ORIGINS[0]
  const o = location.origin
  return OWN_ORIGINS.includes(o) || /^http:\/\/localhost(:\d+)?$/.test(o) ? o : OWN_ORIGINS[0]
}

export function SignIn() {
  // U0.6: an invite or recovery link has already signed this person in; what
  // they need is a password, not the form that asks for one.
  return useAuth().setup ? <Setup /> : <SignInForm />
}

function SignInForm() {
  const { signInWithPasskey, signInWithPassword, linkError } = useAuth()
  // C2.3: the region being looked at. Signed out nothing says otherwise, so
  // this is the home region's name (the root resolves it before any screen).
  const regionName = currentRegion(useRegionsNow()).name
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(linkError)
  const [sent, setSent] = useState(false)

  async function run(fn: () => Promise<string | null>) {
    setBusy(true); setError(null); setSent(false)
    setError(await fn())
    setBusy(false)
  }

  // "Forgotten your password?": Supabase mails a recovery link back to this
  // origin; arriving by it is the `Setup` state below. Supabase answers the
  // same whether or not the address is known, and so does this.
  async function forgotten() {
    if (!email) { setSent(false); setError('Enter your email above first.'); return }
    setBusy(true); setError(null); setSent(false)
    const { error: e } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: resetRedirect() })
    if (e) setError(e.message); else setSent(true)
    setBusy(false)
  }

  return (
    // `.gate` is shared with `App.tsx`'s own not-configured/loading screens
    // (never mounted at the same time as this one) — `scripts/ui-audit`'s
    // harness waits on `.gate .stack` to know the sign-in screen is ready
    // and reads `.gate p.err` on a failed attempt, so both stay. The box is
    // a plain div, not a `Card`: Doug asked for no border around sign-in,
    // and the mark above the buttons is the name.
    <div className="gate">
      <div className="signincard">
        <KiezMark className="signincard__mark" />
        <h1 className="signincard__name">Kiez</h1>
        <p className="signincard__line">
          {regionName}&rsquo;s concerts, films, clubs, talks and exhibitions, picked for you.
          Private; by invitation.
        </p>

        <div className="stack">
          <Button block disabled={busy} onClick={() => run(signInWithPasskey)}>
            Sign in with a passkey
          </Button>

          {!showPassword && (
            <Button variant="quiet" onClick={() => setShowPassword(true)}>
              Use a password instead
            </Button>
          )}

          {/* a2-41: a real form, so Enter (or the keyboard's Go) submits and
              a password manager recognises it; the email field takes focus
              when it appears rather than leaving it on <body>. */}
          {showPassword && (
            <form
              className="stack"
              onSubmit={e => {
                e.preventDefault()
                if (!busy && email && password) void run(() => signInWithPassword(email, password))
              }}
            >
              <Field label="Email">
                <Field.Input type="email" value={email} autoComplete="username" autoFocus
                       onChange={e => setEmail(e.target.value)} />
              </Field>
              <Field label="Password">
                <Field.Input type="password" value={password} autoComplete="current-password"
                       onChange={e => setPassword(e.target.value)} />
              </Field>
              <Button type="submit" variant="secondary" block disabled={busy || !email || !password}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
              <Button variant="quiet" disabled={busy} onClick={() => void forgotten()}>
                Forgotten your password?
              </Button>
            </form>
          )}

          {sent && <p className="signincard__sent" role="status">Check your mail.</p>}

          {/* Announced: an error that appears silently is one VoiceOver never reads. */}
          {error && <p className="err" role="alert">{error}</p>}
        </div>

        <details className="signincard__privacy">
          <summary>Privacy</summary>
          <p>{PRIVACY_NOTE}</p>
        </details>
      </div>
    </div>
  )
}

/**
 * U0.6: where an invite (or a password-recovery) link lands. supabase-js has
 * made the session from the link's hash; two steps follow, then the app. The
 * passkey step is the Account sheet's offer, asked once here because a new
 * person would otherwise never find it. Same `.gate` and `.signincard`, so
 * the heading is the only thing that differs from the sign-in screen.
 */
/** `.gate h1` sets its own margins; inside the stack the gap does the spacing. */
const HEADING = { margin: 0, textAlign: 'center' as const }

function Setup() {
  const { session, loading, setup, setPassword, registerPasskey, finishSetup } = useAuth()
  const [password, setPasswordText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (loading) return <div className="gate"><div className="box"><p>Loading…</p></div></div>

  // The link was good enough to be read and not to be kept: no session came
  // of it. Say so, and leave by the ordinary sign-in.
  if (!session) {
    return (
      <div className="gate">
        <div className="signincard">
          <KiezMark className="signincard__mark" />
          <div className="stack">
            <p className="err" role="alert">That link has expired or was already used. Ask for a new one.</p>
            <Button variant="secondary" block onClick={finishSetup}>Back to sign in</Button>
          </div>
        </div>
      </div>
    )
  }

  async function run(fn: () => Promise<string | null>) {
    setBusy(true); setError(null)
    const e = await fn()
    setError(e === CANCELLED ? null : e)
    setBusy(false)
    return e
  }

  return (
    <div className="gate">
      <div className="signincard">
        <KiezMark className="signincard__mark" />

        {setup === 'password' ? (
          <div className="stack">
            <h1 style={HEADING}>{LINK_KIND === 'recovery' ? 'Choose a new password' : 'Set a password'}</h1>
            <form
              className="stack"
              onSubmit={e => {
                e.preventDefault()
                if (!busy && password) void run(() => setPassword(password))
              }}
            >
              <Field label="Password">
                <Field.Input type="password" value={password} autoComplete="new-password" autoFocus
                       onChange={e => setPasswordText(e.target.value)} />
              </Field>
              <Button type="submit" block disabled={busy || !password}>
                {busy ? 'Saving…' : 'Continue'}
              </Button>
              {error && <p className="err" role="alert">{error}</p>}
            </form>
          </div>
        ) : (
          <div className="stack">
            <h1 style={HEADING}>Add a passkey?</h1>
            <div className="stack">
              <Button block disabled={busy} onClick={() => void run(async () => {
                const e = await registerPasskey()
                if (!e) finishSetup()
                return e
              })}>
                Add a passkey
              </Button>
              <Button variant="quiet" disabled={busy} onClick={finishSetup}>Not now</Button>
              {error && <p className="err" role="alert">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
